import { createReducer, on } from '@ngrx/store';

import { CrisisFilesStoreModel, CrisisOutageDetailsStoreModel } from '@domain/crisis-management';
import { MessageStoreModel } from '@domain/messages';

import { CrisisActions } from './crisis.actions';
import { CrisisCurtailmentStoreModel } from './models';

export const featureKey = 'crisis';

export interface AppState {
  [featureKey]: FeatureState;
}

export interface FeatureState {
  isCrisis: boolean;
  isLoading: boolean;
  isExecuteCurtailmentLoading: boolean;
  isExecuteCurtailmentFail: boolean;
  isOutageDetailsLoading: boolean;
  isActiveTasksLoading: boolean;
  isCompletedTasksLoading: boolean;
  isCurtailmentLoading: boolean;
  isFilesLoading: boolean;
  outageDetails?: CrisisOutageDetailsStoreModel;
  curtailment?: CrisisCurtailmentStoreModel;
  messages?: MessageStoreModel[];
  isMessagesLoading: boolean;
  files?: CrisisFilesStoreModel;
}

export const initialState: FeatureState = {
  isCrisis: false,
  isLoading: false,
  isExecuteCurtailmentLoading: false,
  isExecuteCurtailmentFail: false,
  isOutageDetailsLoading: false,
  isActiveTasksLoading: false,
  isCompletedTasksLoading: false,
  isCurtailmentLoading: false,
  isFilesLoading: false,
  outageDetails: undefined,
  curtailment: undefined,
  messages: undefined,
  isMessagesLoading: false,
  files: undefined,
};

export const crisisReducer = createReducer(
  initialState,

  on(
    CrisisActions.clearCrisis,
    (state): FeatureState => ({
      ...initialState,
      isCrisis: state.isCrisis,
      isExecuteCurtailmentFail: false,
      isExecuteCurtailmentLoading: false,
    })
  ),

  on(CrisisActions.checkCrisis, (state): FeatureState => ({ ...state })),
  on(CrisisActions.checkCrisisSuccess, (state): FeatureState => ({ ...state, isCrisis: true })),
  on(CrisisActions.checkCrisisFail, (state): FeatureState => ({ ...state, isCrisis: false })),

  on(CrisisActions.getOutageDetails, (state): FeatureState => ({ ...state, isOutageDetailsLoading: true })),
  on(
    CrisisActions.getOutageDetailsSuccess,
    (state, { outageDetails }): FeatureState => ({
      ...state,
      outageDetails,
      isOutageDetailsLoading: false,
    })
  ),
  on(CrisisActions.getOutageDetailsFail, (state): FeatureState => ({ ...state, isOutageDetailsLoading: false })),

  on(
    CrisisActions.getCrisisCurtailment,
    (state, { deliveryDayDateTime }): FeatureState => ({
      ...state,
      isCurtailmentLoading: true,
      curtailment: state.curtailment
        ? {
            ...state.curtailment,
            deliveryDayDateTime: deliveryDayDateTime ?? state.curtailment?.deliveryDayDateTime,
          }
        : {
            deliveryDayDateTime: deliveryDayDateTime,
          },
    })
  ),
  on(
    CrisisActions.getCrisisCurtailmentSuccess,
    (state, { mtus, affectedTimescales }): FeatureState => ({
      ...state,
      isCurtailmentLoading: false,
      curtailment: {
        ...state.curtailment,
        mtus,
        affectedTimescales,
      },
    })
  ),
  on(CrisisActions.getCrisisCurtailmentFail, (state): FeatureState => ({ ...state, isCurtailmentLoading: false })),

  on(
    CrisisActions.executeCrisisCurtailment,
    (state): FeatureState => ({
      ...state,
      isLoading: true,
      isExecuteCurtailmentLoading: true,
      isExecuteCurtailmentFail: false,
    })
  ),
  on(
    CrisisActions.executeCrisisCurtailmentSuccess,
    (state): FeatureState => ({
      ...state,
      isLoading: false,
      isExecuteCurtailmentLoading: false,
    })
  ),
  on(
    CrisisActions.executeCrisisCurtailmentFail,
    (state): FeatureState => ({
      ...state,
      isLoading: false,
      isExecuteCurtailmentLoading: false,
      isExecuteCurtailmentFail: true,
    })
  ),

  on(CrisisActions.endUnplannedOutage, (state): FeatureState => ({ ...state, isLoading: true })),
  on(CrisisActions.endUnplannedOutageSuccess, (state): FeatureState => ({ ...state, isLoading: false })),
  on(CrisisActions.endUnplannedOutageFail, (state): FeatureState => ({ ...state, isLoading: false })),

  on(CrisisActions.resendRemit, (state): FeatureState => ({ ...state, isLoading: true })),
  on(CrisisActions.resendRemitSuccess, (state): FeatureState => ({ ...state, isLoading: false })),
  on(CrisisActions.resendRemitFail, (state): FeatureState => ({ ...state, isLoading: false })),

  on(CrisisActions.bypassRemit, (state): FeatureState => ({ ...state, isLoading: true })),
  on(CrisisActions.bypassRemitSuccess, (state): FeatureState => ({ ...state, isLoading: false })),
  on(CrisisActions.bypassRemitFail, (state): FeatureState => ({ ...state, isLoading: false })),

  on(CrisisActions.getCrisisMessages, (state): FeatureState => ({ ...state, isMessagesLoading: true })),
  on(
    CrisisActions.getCrisisMessagesSuccess,
    (state, { messages }): FeatureState => ({
      ...state,
      messages,
      isMessagesLoading: false,
    })
  ),
  on(CrisisActions.getCrisisMessagesFail, (state): FeatureState => ({ ...state, isMessagesLoading: false })),

  on(CrisisActions.postCrisisMessage, (state): FeatureState => ({ ...state, isLoading: true })),
  on(
    CrisisActions.postCrisisMessageSuccess,
    CrisisActions.postCrisisMessageFail,
    (state): FeatureState => ({ ...state, isLoading: false })
  ),

  on(CrisisActions.getCrisisFiles, (state): FeatureState => ({ ...state, isFilesLoading: true })),
  on(
    CrisisActions.getCrisisFilesSuccess,
    (state, { crisisFiles }): FeatureState => ({
      ...state,
      isFilesLoading: false,
      files: crisisFiles,
    })
  ),
  on(CrisisActions.getCrisisFilesFail, (state): FeatureState => ({ ...state, isFilesLoading: false }))
);
